import {
    AfterViewInit,
    Component,
    ComponentRef,
    ContentChild,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    Type,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ReflectionService } from '../../shared/utilities/reflection.service';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { ChipListSelectComponent } from '../chip-list-select/chip-list-select.component';
import { ChipListComponent } from '../chip-list/chip-list.component';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { DateRangePickerComponent } from '../date-range-picker/date-range-picker.component';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import {
    ChipListInputType,
    InputValueChange,
    QuestionComponents,
    QuestionDefinition,
    QuestionValueChange
} from '../form.types';
import { InputViewComponent } from '../input-view/input-view.component';
import { InputComponent } from '../input/input.component';
import { PromptComponent } from '../prompt/prompt.component';
import { QuestionStateService } from '../question-state.service';
import { RichTextInputComponent } from '../rich-text-input/rich-text-input.component';
import { SelectComponent } from '../select/select.component';
import { TextareaComponent } from '../textarea/textarea.component';
import { QuestionService } from './question.service';

@Component({
    selector: 'app-question',
    templateUrl: './question.component.html',
    styleUrls: ['./question.component.scss'],
    encapsulation: ViewEncapsulation.Emulated
})
export class QuestionComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() question: QuestionDefinition;
    @Output() controlValueChange: EventEmitter<QuestionValueChange> = new EventEmitter<QuestionValueChange>();
    @ViewChild('wrapperContainer', { static: true, read: ViewContainerRef }) wrapperContainer: ViewContainerRef;
    @ViewChild('questionTemplate', { static: true, read: ViewContainerRef }) questionContainer: ViewContainerRef;
    @ViewChild('promptTemplate', { static: true, read: ViewContainerRef }) promptTemplate: ViewContainerRef;
    @ViewChild('promptContainer', { static: true, read: ViewContainerRef }) promptContainer: ViewContainerRef;
    wrapperClasses: string;
    component: ComponentRef<QuestionComponents>;
    dependantComponent: ComponentRef<QuestionComponents>;
    private subscription: Subscription;
    private promptComponents: Map<number, ComponentRef<PromptComponent>>;
    private inputIdSubscription$: Subscription;
    private questionState$: Subscription;
    inputId: string;

    constructor(
        private reflection: ReflectionService,
        private questionService: QuestionService,
        private renderer2: Renderer2,
        private questionState: QuestionStateService
    ) {}

    ngOnInit(): void {
        this.generatePrompts();
        this.component = this.createQuestion(this.questionContainer, this.question);
        if (this.question.questionStateListener) {
            this.subscribe();
        }
    }

    ngAfterViewInit() {
        if (this.promptContainer && this.inputId) {
            this.renderer2.setAttribute(
                this.promptContainer.element.nativeElement,
                'id',
                `${this.inputId}-edit-prompt`
            );
        }
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.questionState$) {
            this.questionState$.unsubscribe();
        }
        if (this.component) {
            this.component.destroy();
        }
        if (this.dependantComponent) {
            this.dependantComponent.destroy();
        }
        if (this.promptComponents) {
            this.promptComponents.forEach((prompt) => {
                if (prompt) {
                    prompt.destroy();
                }
            });
        }
        if (this.inputIdSubscription$) {
            this.inputIdSubscription$.unsubscribe();
        }
    }

    subscribe() {
        this.questionState$ = this.questionState.questionStateObservable.subscribe((next) => {
            if (
                next &&
                this.question.questionStateListener.alias &&
                next.question.questionStateListener.alias === this.question.questionStateListener.alias
            ) {
                if (next.updates) {
                    this.question = this.reflection.updateProperties(this.question, next.updates);
                }
                if (next.regeneratePrompts) {
                    this.generatePrompts();
                }
            }
        });
    }

    createComponent<T>(container: ViewContainerRef, type: Type<T>): ComponentRef<T> {
        return container.createComponent(type);
    }

    createQuestion(container: ViewContainerRef, question: QuestionDefinition): ComponentRef<QuestionComponents> {
        if (!question || !question.input || !question.input.control || !container) {
            return;
        }
        const c = this.reflection.collectArray(this?.question?.classes);
        this.wrapperClasses = c && c.length ? 'question ' + c.join(' ') : 'question';
        let component: ComponentRef<QuestionComponents>;
        switch (question.input.discriminator) {
            case 'TEXT': {
                component = this.createComponent(container, InputComponent);
                component.instance.input = this.questionService.toText(question.input);
                break;
            }
            case 'TEXT-VIEW': {
                component = this.createComponent(container, InputViewComponent);
                component.instance.input = this.questionService.toTextView(question.input);
                break;
            }
            case 'TEXTAREA': {
                component = this.createComponent(container, TextareaComponent);
                component.instance.input = this.questionService.toTextArea(question.input);
                break;
            }
            case 'SELECT': {
                component = this.createComponent(container, SelectComponent);
                component.instance.input = this.questionService.toSelect(question.input);
                break;
            }
            case 'DATE': {
                component = this.createComponent(container, DatePickerComponent);
                component.instance.input = this.questionService.toDate(question.input);
                break;
            }
            case 'DATE-RANGE': {
                component = this.createComponent(container, DateRangePickerComponent);
                component.instance.input = this.questionService.toDateRange(question.input);
                break;
            }
            case 'DATE-TIME': {
                component = this.createComponent(container, DateTimePickerComponent);
                component.instance.input = this.questionService.toDateTime(question.input);
                break;
            }
            case 'AUTOCOMPLETE': {
                component = this.createComponent(container, AutocompleteComponent);
                component.instance.input = this.questionService.toAutoComplete(question.input);
                break;
            }
            case 'CHECKBOX': {
                component = this.createComponent(container, CheckboxComponent);
                component.instance.input = this.questionService.toCheckBox(question.input);
                break;
            }
            case 'CHIP-LIST': {
                const input: ChipListInputType = this.questionService.toChipList(question.input);
                if (input.basicList === true) {
                    component = this.createComponent(container, ChipListComponent);
                } else {
                    component = this.createComponent(container, ChipListSelectComponent);
                }
                component.instance.input = input;
                break;
            }
            case 'RICH-TEXT': {
                component = this.createComponent(container, RichTextInputComponent);
                component.instance.input = this.questionService.toRichText(question.input);
            }
        }

        if (component) {
            this.subscription = component.instance.inputValueChangeEventEmitter.subscribe((next: InputValueChange) => {
                this.controlValueChange.emit({
                    payload: this.question.input.control.value,
                    question: question,
                    inputValueChange: next
                } as QuestionValueChange);
                this.generatePrompts();
            });
            this.inputIdSubscription$ = component.instance.inputIdEvent.subscribe((next: string) => {
                this.inputId = next;
            });
        }
        return component;
    }

    private generatePrompts(): void {
        if (this?.question?.prompts) {
            this.promptComponents = this.promptComponents
                ? this.promptComponents
                : new Map<number, ComponentRef<PromptComponent>>();
            this.question.prompts.forEach((prompt, index) => {
                const createPrompt =
                    !prompt.showCallBack || prompt.showCallBack.function.call(prompt.showCallBack.this);
                if (createPrompt && !this.promptComponents.get(index)) {
                    const promptComponent = this.createComponent(this.promptTemplate, PromptComponent);
                    promptComponent.instance.prompt = prompt;
                    promptComponent.instance.clickEvent.subscribe((mouseEvent: MouseEvent) => {
                        this.component.location.nativeElement.querySelector('[id="' + this.inputId + '"]').focus();
                    });
                    this.promptComponents.set(index, promptComponent);
                } else if (!createPrompt && this.promptComponents.get(index)) {
                    this.promptComponents.get(index).destroy();
                    this.promptComponents.delete(index);
                }
            });
        }
    }
}
